<template>
  <b-modal ref="modal" :id="modalId" title="Upload images" size="lg" body-class="p-0" centered @hidden="onModalHidden">
    <div class="px-5 py-4 pb-5 modal-body-content">
      <image-file-input
        class="m-0"
        :id="`${modalId}-file-input`"
        multiple
        upload-text="Drag and Drop or <u>Click to upload</u>"
        :supported-text="
          openBanner
            ? 'JPG or PNG • up to 10 MB each • Recommended 1600 × 400 px (4:1)'
            : 'JPG or PNG • up to 10 MB each • Recommended 1600 × 900 px (16:9)'
        "
        @change="onInputChange"
      />
      <div v-if="pendingFiles.length" class="mt-3 preview-grid" :class="{ 'preview-grid--open-banner': openBanner }">
        <div
          v-for="(item, index) in pendingFiles"
          :key="item.previewUrl"
          class="preview-thumb"
          :class="{ 'preview-thumb--open-banner': openBanner }"
        >
          <img :src="item.previewUrl" :alt="item.file.name" class="preview-thumb__image" />
          <button type="button" class="preview-thumb__remove" @click="removePending(index)">
            <font-awesome-icon :icon="['fas', 'times']" />
          </button>
        </div>
      </div>
    </div>

    <template #modal-footer>
      <small v-if="error" class="text-danger mr-auto">{{ error }}</small>
      <b-button variant="primary" :disabled="!pendingFiles.length" @click="confirm">
        {{ confirmLabel }}
      </b-button>
    </template>
  </b-modal>
</template>

<script setup>
import { computed, ref } from 'vue';
import ImageFileInput from '@/pages/recognition/components/ImageFileInput.vue';

const props = defineProps({
  modalId: { type: String, required: true },
  acceptedFileTypes: { type: Array, default: () => ['image/jpeg', 'image/png'] },
  maxSizeBytes: { type: Number, default: 10 * 1024 * 1024 },
  maxFiles: { type: Number, default: Infinity },
  // Open-award banners crop to 4:1 in the app; program/milestone banners keep 16:9.
  openBanner: { type: Boolean, default: false },
});

const emit = defineEmits(['select']);

const modal = ref(null);
const pendingFiles = ref([]);
const error = ref('');

const confirmLabel = computed(() =>
  pendingFiles.value.length > 1 ? `Use ${pendingFiles.value.length} images` : 'Use image',
);

const revokeObjectUrl = url => {
  if (url) {
    try {
      URL.revokeObjectURL(url);
    } catch {
      // ignore
    }
  }
};

const resetState = () => {
  error.value = '';
  pendingFiles.value.forEach(item => revokeObjectUrl(item.previewUrl));
  pendingFiles.value = [];
};

const onModalHidden = () => {
  resetState();
};

const onInputChange = event => {
  const files = [...(event?.target?.files || event?.dataTransfer?.files || [])];
  if (!files.length) {
    return;
  }
  const valid = [];
  let rejectionReason = '';
  for (const file of files) {
    if (file.size > props.maxSizeBytes) {
      rejectionReason = rejectionReason || 'Each image must be 10MB or less.';
      continue;
    }
    if (file.type && !props.acceptedFileTypes.includes(file.type)) {
      rejectionReason = rejectionReason || 'Only JPG or PNG images are allowed.';
      continue;
    }
    valid.push(file);
  }
  const remainingSlots = props.maxFiles - pendingFiles.value.length;
  const accepted = valid.slice(0, Math.max(0, remainingSlots));
  if (valid.length > remainingSlots) {
    error.value = `You can add up to ${props.maxFiles} images.`;
  } else if (accepted.length < files.length) {
    error.value = `${files.length - accepted.length} file(s) skipped: ${rejectionReason}`;
  } else {
    error.value = '';
  }
  pendingFiles.value = [
    ...pendingFiles.value,
    ...accepted.map(file => ({ file, previewUrl: URL.createObjectURL(file) })),
  ];
};

const removePending = index => {
  const [removed] = pendingFiles.value.splice(index, 1);
  if (removed) {
    revokeObjectUrl(removed.previewUrl);
  }
};

const confirm = () => {
  if (!pendingFiles.value.length) {
    return;
  }
  const files = pendingFiles.value.map(item => item.file);
  // The parent creates its own preview URL for each file, so these are redundant.
  pendingFiles.value.forEach(item => revokeObjectUrl(item.previewUrl));
  pendingFiles.value = [];
  emit('select', files);
  modal.value?.hide();
};
</script>

<style scoped>
:deep(.modal-title) {
  font-weight: 600;
  font-size: 1rem;
  line-height: 1.5rem;
  width: 100%;
  text-align: center;
  margin-left: 1.25rem;
}

:deep(.modal-header) {
  border-bottom: 1px solid var(--palette-color-default-lighten-90);
}

.modal-body-content {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  border-bottom: 1px solid var(--palette-color-default-lighten-90);
}

.preview-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.75rem;
}

.preview-grid--open-banner {
  grid-template-columns: repeat(2, 1fr);
}

.preview-thumb {
  position: relative;
  aspect-ratio: 16 / 9;
  border-radius: 0.5rem;
  overflow: hidden;
  background: var(--palette-color-core-1-lighten-90);
}

.preview-thumb--open-banner {
  aspect-ratio: 4 / 1;
}

.preview-thumb__image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.preview-thumb__remove {
  position: absolute;
  top: 0.375rem;
  right: 0.375rem;
  width: 1.5rem;
  height: 1.5rem;
  border: none;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.6);
  color: var(--palette-color-base-white);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 0.75rem;
}
</style>
